import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { profile, resolvePath, sso as ssoPath } from '../../routes';
import { accountSetting, accountSubscriptionManagement, userManagement } from '../../layout/routes';
import { isAdmin, isOwner, isBillingManager, AccountRole } from '../../models';
import { useLaunchDarkly } from '../../launchdarkly';

const useStyles = makeStyles(theme => ({
  boxRow: {
    flexDirection: 'row',
    minWidth: '22.5%',
    height: theme.spacing(38.5),
    marginTop: theme.spacing(3),
  },
  boxColumn: {
    display: 'column',
  },
  paperRoot: {
    minWidth: theme.spacing(30),
    maxWidth: theme.spacing(45),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
  },
  link: {
    color: '#233145',
  },
  subTitle: {
    fontSize: theme.spacing(2.5),
    marginBottom: theme.spacing(2.75),
    fontWeight: theme.typography.fontWeightMedium,
    color: '#233145',
  },
  titleLink: {
    marginTop: theme.spacing(1.5),
    color: '#233145',
  },
}));

interface QuickActionProps {
  organizationId: number;
  accountId: number;
  role: AccountRole;
}

const QuickAction = (prop: QuickActionProps) => {
  const { organizationId, accountId, role } = prop;
  const classes = useStyles();
  const { flags } = useLaunchDarkly();

  const manageLicenses = (
    <Typography variant="body2" className={classes.titleLink}>
      <Link
        id="admin.home.link.manage.my.licenses"
        className={classes.link}
        component={RouterLink}
        to={resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${organizationId}/admin/license_keys/kse` })}
        underline="always"
      >
        <FormattedMessage id="admin.home.link.manage.my.licenses" />
      </Link>
    </Typography>
  );

  const subscribeProducts = (
    <Typography variant="body2" className={classes.titleLink}>
      <Link
        id="admin.home.link.subscribe.to.katalon.products"
        className={classes.link}
        component={RouterLink}
        to={resolvePath(accountSubscriptionManagement, undefined, { accountId })}
        underline="always"
      >
        <FormattedMessage id="admin.home.link.subscribe.to.katalon.products" />
      </Link>
    </Typography>
  );

  const inviteUsers = (
    <Typography variant="body2" className={classes.titleLink}>
      <Link
        id="admin.home.link.invite.users.to.account"
        className={classes.link}
        component={RouterLink}
        to={resolvePath(userManagement, undefined, { orgId: `${organizationId}` })}
        underline="always"
      >
        <FormattedMessage id="admin.home.link.invite.users.to.account" />
      </Link>
    </Typography>
  );

  const updateProfile = (
    <Typography variant="body2" className={classes.titleLink}>
      <Link
        id="admin.home.link.update.profile"
        className={classes.link}
        component={RouterLink}
        to={resolvePath(profile)}
        underline="always"
      >
        <FormattedMessage id="admin.home.link.update.profile" />
      </Link>
    </Typography>
  );

  const getAccountId = (
    <Typography variant="body2" className={classes.titleLink}>
      <Link
        id="admin.home.link.get.the.account.id"
        className={classes.link}
        component={RouterLink}
        to={resolvePath(accountSetting, undefined, { accountId, orgId: organizationId })}
        underline="always"
      >
        <FormattedMessage id="admin.home.link.get.the.account.id" />
      </Link>
    </Typography>
  );

  return (
    <Box className={classes.boxRow} justifyContent="space-between">
      <Paper elevation={1} className={classes.paperRoot}>
        {isOwner(role) && (
          <Box className={classes.boxColumn}>
            <Typography variant="body1" className={classes.subTitle}>
              <FormattedMessage id="admin.home.title.quick.actions" />
            </Typography>
            {manageLicenses}
            {flags && !flags.subscriptionAndPaymentMethodDisabled && subscribeProducts}
            {inviteUsers}
            {getAccountId}
            {updateProfile}
          </Box>
        )}

        {isAdmin(role) && (
          <Box className={classes.boxColumn}>
            <Typography variant="body1" className={classes.subTitle}>
              <FormattedMessage id="admin.home.title.quick.actions" />
            </Typography>
            {manageLicenses}
            {inviteUsers}
            {getAccountId}
            {updateProfile}
          </Box>
        )}

        {isBillingManager(role) && (
          <Box className={classes.boxColumn}>
            <Typography variant="body1" className={classes.subTitle}>
              <FormattedMessage id="admin.home.title.quick.actions" />
            </Typography>
            {/* Hide View license when new navigation UI is enabled */}
            {!flags?.newNavigationUIEnabled && (
              <Typography variant="body2" className={classes.titleLink}>
                <Link className={classes.link} component={RouterLink} to={resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${organizationId}/admin/license_keys/kse` })} underline="always">
                  <FormattedMessage id="admin.home.link.view.licenses" />
                </Link>
              </Typography>
            )}
            {subscribeProducts}
            {getAccountId}
            {updateProfile}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default QuickAction;
